import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { db } from "astrid-firebase";
import organizationTypes from "astrid-firestore/src/api/organizations/constants/organizationTypes";
import useCollectionDataOnce from "astrid-firestore/src/hooks/useCollectionDataOnce";
import useDocumentDataOnce from "astrid-firestore/src/hooks/useDocumentDataOnce";
import useLoadingValues from "astrid-firestore/src/hooks/useLoadingValues";

import LoadingContext from "../../../ui/components/LoadingContext/LoadingContext";
import PageView from "../../../ui/views/PageView/PageView";

import { useProfile } from "../../../authentication/state/profile";
import featureFlags from "../../../authorization/constants/featureFlags";
import useFeatureFlag from "../../../authorization/hooks/useFeatureFlag";
import useOrganizationsQuery from "../../../organizations/hooks/useOrganizationsQuery";
import useSelectOrganization from "../../../organizations/hooks/useSelectOrganization";
import useUserOrganizations from "../../../organizations/hooks/useUserOrganizations";

import CreateProductionPublisherForm from "../../components/CreateProductionForm/CreateProductionPublisherForm";
import CreateProductionPublisherFormNew from "../../components/CreateProductionForm/CreateProductionPublisherFormNew";

export default function CreateProductionPublisherView({ organization, articleId, workId }) {
	const { t } = useTranslation();
	const user = useProfile();
	const selectOrganization = useSelectOrganization();

	const hasFlatteningFeature = useFeatureFlag(featureFlags.FLATTENING, organization);

	const [[work, article], loading, error] = useLoadingValues(
		useDocumentDataOnce(workId && db.collection("works").doc(workId)),
		useDocumentDataOnce(articleId && db.collection("articles").doc(articleId)),
	);

	// change global organization when publisher is changed in form
	const setPublisherId = useCallback(
		(id) => {
			if (id !== organization.id) {
				selectOrganization({ id, type: organizationTypes.PUBLISHER });
			}
		},
		[organization.id, selectOrganization],
	);

	// get producers for publisher
	const [producers] = useCollectionDataOnce(
		useOrganizationsQuery({
			publisher: organization.id,
			type: organizationTypes.PRODUCER,
		}),
	);

	const publisherOptions = useUserOrganizations(user)
		.filter(({ type }) => type === organizationTypes.PUBLISHER)
		.map(({ id, name }) => ({ key: id, value: id, text: name }));

	if (!publisherOptions.find(({ value }) => value === organization.id)) {
		publisherOptions.push({
			key: organization.id,
			value: organization.id,
			text: organization.name,
		});
	}

	const producerOptions = producers?.map(({ id, name }) => ({ key: id, value: id, text: name }));

	if (!producerOptions) {
		return null;
	}

	return (
		<PageView maxWidth={800} header={t("createNewProd")}>
			{hasFlatteningFeature ? (
				<LoadingContext loading={loading} error={error}>
					<CreateProductionPublisherFormNew publisher={organization} work={work} article={article} />
				</LoadingContext>
			) : (
				<CreateProductionPublisherForm
					defaultWorkId={workId}
					defaultArticleId={articleId}
					defaultProducerId={producerOptions?.length === 1 ? producerOptions[0].value : null}
					defaultPublisherId={organization.id}
					producerOptions={producerOptions}
					publisherOptions={publisherOptions}
					setPublisherId={setPublisherId}
				/>
			)}
		</PageView>
	);
}
